import React, { useEffect, useMemo, useState } from "react";
import { CCButton, CCField, CCModal } from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { SearchHit, useFederatedSearch } from "../../hooks/useFederatedSearch";
import { AttendeeAddOutcome } from "../../hooks/useEventAttendees";
import "./eventManager.css";

interface EventAttendeeAddDialogProps {
	open: boolean;
	saving: boolean;
	/** Already on the roll — shown as such, and not addable twice. */
	existingIds: Set<string>;
	onClose: () => void;
	onAdd: (
		contactIds: string[],
		type: "Attendee" | "Supervisor"
	) => Promise<AttendeeAddOutcome>;
}

/** The API's ceiling on one POST. */
const MAX_PER_REQUEST = 100;

/**
 * Search for people and add them to the event's roll.
 *
 * ⚠️ PII. Shows student names and year levels. Held in component state for the
 * life of the dialog, cleared on close, never logged or persisted.
 *
 * Why a search rather than the group picker the button used to open: attendees
 * mostly arrive with a group, but an event always ends up with the one student
 * who joined late, and sending someone to the Groups tab to add a single person
 * meant either finding a group that happens to hold just her or not doing it at
 * all. Groups remain the bulk route; this is the exception.
 *
 * Adding does not approve anyone. A new attendee starts undecided, and the
 * decision is still the carer's to give.
 *
 * The search is My Pymble's federated search, restricted to the student scope —
 * the same one behind the portal navbar, so a name found there is found here.
 * It already returns a display title and a subtitle, so this no longer builds
 * either from the discrete name fields.
 */
export const EventAttendeeAddDialog: React.FC<EventAttendeeAddDialogProps> = ({
	open,
	saving,
	existingIds,
	onClose,
	onAdd,
}) => {
	const { term, setTerm, longEnough, minQueryLength, hits, searching, searchFailed } =
		useFederatedSearch(["student"]);

	/** Picked people, kept whole so the chips can show names after the search moves on. */
	const [picked, setPicked] = useState<SearchHit[]>([]);
	const [type, setType] = useState<"Attendee" | "Supervisor">("Attendee");
	const [error, setError] = useState("");

	// Reset on open, so a previous session's selection cannot be added to the
	// wrong event, and so names do not linger in state after the dialog closes.
	useEffect(() => {
		if (open) {
			setPicked([]);
			setType("Attendee");
			setError("");
			setTerm("");
		}
	}, [open, setTerm]);

	const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);

	const toggle = (contact: SearchHit) => {
		setError("");
		setPicked((prev) =>
			prev.some((p) => p.id === contact.id)
				? prev.filter((p) => p.id !== contact.id)
				: prev.concat(contact)
		);
	};

	const submit = async () => {
		if (!picked.length) {
			setError("Choose at least one person to add.");
			return;
		}
		if (picked.length > MAX_PER_REQUEST) {
			setError(`Add at most ${MAX_PER_REQUEST} people at a time.`);
			return;
		}
		setError("");
		// The caller owns the result toast and closing — a partial add is a normal
		// outcome here and it has the counts to describe it.
		await onAdd(
			picked.map((p) => p.id),
			type
		);
	};

	return (
		<CCModal
			open={open}
			onClose={onClose}
			title="Add attendees"
			footer={
				<>
					<CCButton variant="outline" onClick={onClose} disabled={saving}>
						Cancel
					</CCButton>
					<CCButton
						variant="solid"
						colour="primary"
						onClick={() => void submit()}
						disabled={saving || !picked.length}
					>
						{saving
							? "Adding…"
							: `Add ${picked.length || ""} ${
									picked.length === 1 ? "person" : "people"
								}`.replace("  ", " ")}
					</CCButton>
				</>
			}
		>
			<div className="em-att-add">
				{/* CCField draws its own notched label, so there is no separate
				    .em-label here — two labels would be announced twice. */}
				<CCField
					id="em-attendee-search"
					label="Search for a student"
					type="search"
					size="sm"
					fullWidth
					placeholder="Name…"
					value={term}
					disabled={saving}
					onChange={(e) => setTerm(e.target.value)}
				/>

				{/* The picked list sits above the results so it stays visible while
				    the search below it changes underneath. */}
				{picked.length > 0 && (
					<ul className="em-att-add__picked">
						{picked.map((p) => (
							<li key={p.id}>
								<span>{p.title}</span>
								<button
									type="button"
									aria-label={`Remove ${p.title}`}
									disabled={saving}
									onClick={() => toggle(p)}
								>
									<FontAwesomeIcon icon={faXmark} />
								</button>
							</li>
						))}
					</ul>
				)}

				<div className="em-att-add__results" role="listbox" aria-label="Search results">
					{!longEnough ? (
						<p className="em-muted">
							Type at least {minQueryLength} characters to search.
						</p>
					) : searchFailed ? (
						<p className="em-error" role="alert">
							Could not run that search. Please try again.
						</p>
					) : searching && !hits.length ? (
						<p className="em-muted">Searching…</p>
					) : !hits.length ? (
						<p className="em-muted">No matching students.</p>
					) : (
						hits.map((c) => {
							const already = existingIds.has(c.id);
							const chosen = pickedIds.has(c.id);
							return (
								<button
									key={c.id}
									type="button"
									role="option"
									aria-selected={chosen}
									className={`em-att-add__result${chosen ? " is-chosen" : ""}`}
									// Already on the roll: shown rather than hidden, so
									// searching for someone who is already invited answers
									// the question instead of looking like a failed search.
									disabled={already || saving}
									onClick={() => toggle(c)}
								>
									<span className="em-att-add__name">{c.title}</span>
									<span className="em-muted">
										{already ? "Already an attendee" : c.subtitle}
									</span>
								</button>
							);
						})
					)}
				</div>

				<fieldset className="em-create-categories">
					<legend className="em-label">Add as</legend>
					{(["Attendee", "Supervisor"] as const).map((option) => (
						<label key={option} className="em-create-category">
							<input
								type="radio"
								name="em-attendee-type"
								value={option}
								checked={type === option}
								disabled={saving}
								onChange={() => setType(option)}
							/>
							{option}
						</label>
					))}
				</fieldset>

				{error && (
					<span className="em-error" role="alert">
						{error}
					</span>
				)}

				<p className="em-muted">
					Adding does not approve anyone — a new attendee starts undecided.
				</p>
			</div>
		</CCModal>
	);
};
